/* eslint-disable max-lines */
import moment from 'moment'
import { v4 } from 'uuid'

import { useRef, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'

import localizationIcon from '@assets/images/observation-localization-icon.svg'

import {
  getTaskInputFromTaskWithoutStartRows,
  updateTaskByStartDateAndRequiredDuration,
} from '@modules/_shared/helpers/task.helpers'

import { useIOSPreventAutoZooming } from '@utils/hooks/useIOSPreventZooming'

import { useModal } from '@hooks/app/modal.hook'
import { useFilePreviews } from '@hooks/file/file-preview.hook'

import { SelectCompany } from '@modules/_shared/components/SelectCompany'
import { SelectZone } from '@modules/_shared/components/SelectZone'
import { SelectTrade } from '@modules/_shared/components/SelectTrade'
import { PdfMarkerPreview } from '@modules/_shared/components/PdfMarkerPreview'
import { LocalizationEdit } from '@modules/_shared/components/LocalizationEdit'

import { DatePicker } from '@components/DatePicker'
import { DropDown } from '@components/DropDown'
import { Input } from '@components/Input'
import { Modal } from '@components/Modal'

import { useCreateTasks } from '@hooks/planning/create-tasks.hook'

import { Company, ProjectFile, ProjectFilePosition, RemoteFile, Task, TaskStatus, Trade, Zone } from '@types'

import {
  Container,
  DatePickerContainer,
  DatePickerInput,
  SectionContainer,
  LocalizationFormContainer,
  TextButtonLocalization,
  TitleLocalization,
  ViewerContainer,
} from './mobile.add-task.styled'

export const MobileAddTaskPage = (): JSX.Element => {
  const { hideModal } = useModal()
  const { t } = useTranslation()
  const { projectId } = useParams()
  const refScrollView = useRef<HTMLDivElement | null>()
  const [searchParams] = useSearchParams()

  const { createTasks } = useCreateTasks()

  useIOSPreventAutoZooming()

  const [mode, setMode] = useState<'form' | 'localization'>('form')
  const [plan, setPlan] = useState<RemoteFile>()

  const x = searchParams.get('x')
  const y = searchParams.get('y')
  const projectFileId = searchParams.get('projectFileId')

  const initialPosition = x && y ? { x: +x, y: +y } : undefined
  const initialProjectFile = projectFileId ? ({ id: projectFileId } as ProjectFile) : undefined

  const [taskData, setTaskData] = useState<{
    name: string
    startAt: Date | undefined
    requiredDuration: number | undefined
    workforce: number | undefined
    zone: Zone | undefined
    company: Company | undefined
    trade: Trade | undefined
    projectFile?: ProjectFile
    projectFilePosition?: ProjectFilePosition
  }>({
    name: '',
    startAt: moment().toDate(),
    requiredDuration: undefined,
    workforce: undefined,
    zone: undefined,
    company: undefined,
    trade: undefined,
    projectFile: initialProjectFile,
    projectFilePosition: initialPosition,
  })

  const { loadFilePreviews: loadPlanPreview } = useFilePreviews({
    files: taskData?.projectFile?.file ? ([taskData?.projectFile?.file] as RemoteFile[]) : undefined,
    onFilesLoaded: ([file]) => setPlan(file as RemoteFile),
  })

  useEffect(() => {
    if (taskData?.projectFile?.file) {
      loadPlanPreview()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskData?.projectFile])

  const handleSubmit = () => {
    if (!projectId) return

    const taskToAdd: Task = {
      id: v4(),
      idNumber: 0,
      name: taskData.name,
      description: '',
      progress: 0,
      status: TaskStatus.StatusToDo,
      startAt: taskData.startAt!,
      endAt: undefined,
      duration: taskData.requiredDuration!,
      requiredDuration: taskData.requiredDuration!,
      startRowByZone: 0,
      startRowByCompany: 0,
      startRowByTrade: 0,
      workforce: taskData.workforce!,
      zoneId: taskData.zone?.id,
      zone: taskData.zone,
      companyId: taskData.company?.id,
      company: taskData.company,
      tradeKey: taskData.trade!.key,
      trade: taskData.trade!,
      projectFile: taskData.projectFile,
      projectFilePositions: taskData.projectFilePosition
        ? [{ x: taskData.projectFilePosition.x, y: taskData.projectFilePosition.y }]
        : undefined,
      comments: [],
      activities: [],
    }

    const updatedTask = updateTaskByStartDateAndRequiredDuration(
      taskToAdd,
      taskToAdd.startAt,
      taskToAdd.requiredDuration,
    )

    createTasks({
      projectId,
      tasks: [getTaskInputFromTaskWithoutStartRows(updatedTask)],
    })

    hideModal('add_task')
  }

  const handleInputChange = (field: keyof typeof taskData, value: any) => {
    setTaskData((prev) => ({ ...prev, [field]: value }))
  }

  const title =
    mode === 'form'
      ? t('modules_project-suivi_pages_add-task_title')
      : t('modules_project-suivi_pages_add-task_localization_title')

  const hideProjectFilePosition = !!initialPosition

  return (
    <Modal
      refScrollContainer={(ref) => (refScrollView.current = ref)}
      title={title}
      onSubmitLabel={t('modules_project-suivi_pages_add_task_button_label')}
      onSubmit={handleSubmit}
      onClose={() => hideModal('add_task')}
      showPrevious={mode === 'localization'}
      onPrevious={() => setMode('form')}
      showFooter={mode === 'form'}
    >
      {mode === 'form' ? (
        <Container>
          <SectionContainer>
            <Input
              required
              label={t('modules_project-suivi_pages_add_task_label_task-name')}
              placeholder={t('modules_project-suivi_pages_add_task_placeholder_task-name')}
              value={taskData.name}
              onChange={(value) => handleInputChange('name', value)}
            />

            <SelectTrade
              disabledSearch
              required
              trade={taskData.trade}
              onSelectTrade={(value) => handleInputChange('trade', value)}
            />

            <SelectCompany
              disabledAddCompany
              disabledSearch
              projectId={projectId || ''}
              company={taskData.company}
              onSelectCompany={(value) => handleInputChange('company', value)}
            />

            <SelectZone
              disabledSearch
              required
              projectId={projectId || ''}
              zone={taskData.zone}
              onSelectZone={(value) => handleInputChange('zone', value)}
            />

            <DropDown
              disabledVerticalCorrection
              WrappedComponent={({ show }) => (
                <DatePickerInput onClick={show}>
                  <Input
                    type="text"
                    required
                    label={t('modules_project-suivi_pages_add_task_label_star_at')}
                    value={taskData.startAt ? moment(taskData.startAt).format('DD/MM/YYYY') : ''}
                  />
                  <DatePickerContainer />
                </DatePickerInput>
              )}
              WrappedChildren={({ hide }) => (
                <DatePicker
                  selectedDate={taskData.startAt}
                  onChange={(date) => {
                    handleInputChange('startAt', date)
                    hide()
                  }}
                />
              )}
            />

            <Input
              type="number"
              step={0.25}
              min={0.25}
              required
              label={t('modules_project-suivi_pages_add_task_label_duration')}
              placeholder={t('modules_project-suivi_pages_add_task_placeholder_duration')}
              value={taskData.requiredDuration ? `${taskData.requiredDuration}` : ''}
              onChange={(value) => handleInputChange('requiredDuration', parseFloat(value))}
            />

            <Input
              type="number"
              step={0.25}
              min={0}
              required
              label={t('modules_project-suivi_pages_add_task_label_workforce')}
              placeholder={t('modules_project-suivi_pages_add_task_placeholder_workforce')}
              value={taskData.workforce ? `${taskData.workforce || ''}` : ''}
              onChange={(value) => handleInputChange('workforce', parseFloat(value))}
            />

            {!hideProjectFilePosition && (
              <LocalizationFormContainer>
                <TitleLocalization>
                  {t('modules_project-forms_components_EditForm_localization_label')}
                </TitleLocalization>

                {!taskData.projectFilePosition ? (
                  <TextButtonLocalization type="button" onClick={() => setMode('localization')}>
                    <img src={localizationIcon} alt="Localization" />
                    {t('modules_project-forms_components_EditForm_localization_button_label')}
                  </TextButtonLocalization>
                ) : (
                  <ViewerContainer onClick={() => setMode('localization')}>
                    {plan?.url && taskData?.projectFile && (
                      <PdfMarkerPreview
                        pdfUrl={plan.url}
                        initialMarker={{
                          id: v4(),
                          pageNumber: 1,
                          type: 'task',
                          x: taskData.projectFilePosition.x,
                          y: taskData.projectFilePosition.y,
                          data: {
                            name: taskData.name,
                            trade: taskData.trade,
                          },
                        }}
                      />
                    )}
                  </ViewerContainer>
                )}
              </LocalizationFormContainer>
            )}
          </SectionContainer>
        </Container>
      ) : (
        <LocalizationEdit
          id="container"
          markerType="task"
          file={plan}
          projectFile={taskData.projectFile}
          projectId={projectId || ''}
          data={{
            name: taskData.name,
            trade: taskData.trade,
            projectFile: taskData.projectFile,
            projectFilePositions: taskData.projectFilePosition
              ? [
                  {
                    x: taskData.projectFilePosition.x,
                    y: taskData.projectFilePosition.y,
                  },
                ]
              : undefined,
          }}
          onChangeProjectFile={(_, projectFile) => {
            setTaskData((prev) => ({
              ...prev,
              projectFile,
              projectFilePosition: projectFile ? prev.projectFilePosition : undefined,
            }))
          }}
          onAdd={(projectFilePosition: ProjectFilePosition) =>
            setTaskData((prev) => ({ ...prev, projectFilePosition }))
          }
          onChange={(projectFilePosition: ProjectFilePosition) =>
            setTaskData((prev) => ({ ...prev, projectFilePosition }))
          }
          onRemove={() => setTaskData((prev) => ({ ...prev, projectFilePosition: undefined }))}
        />
      )}
    </Modal>
  )
}
